"use client";
import { createContext, useContext, useEffect, useState } from "react";
import useSessionStorage from "@/hooks/useSessionStorage";
import { CommercePath, useBuildCommerceUrl } from "@/hooks/useBuildCommerceUrl";
import { CartData } from "@/utils/cartUtils";
import { useCartContext } from "./CartProvider";

interface ModalContextType {
  showWelcome: boolean;
  showAddToCart: boolean;
  getTotalItems: () => number;
  dismissModal: () => void;
  isOpen: boolean;
  cartData: CartData;
  handleContinueToCheckout: () => void;
  setAddedToCartModalEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setWelcomeBackModalEnabled: React.Dispatch<React.SetStateAction<boolean>>;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

const WELCOME_BACK_MODAL_DISMISSED_KEY = "welcome_back_modal_dismissed";

export const useModalContext = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error("useModalContext must be used within a ModalProvider");
  }
  return context;
};

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    state: { cartData },
    addedToCart,
    shouldShowBadge,
    getTotalItems,
    setAddedToCart,
  } = useCartContext();

  // consumer must set these manually as they are toggled in Monarch
  const [welcomeBackModalEnabled, setWelcomeBackModalEnabled] = useState(false);
  const [addedToCartModalEnabled, setAddedToCartModalEnabled] = useState(false);

  const [showWelcome, setShowWelcome] = useState(false);
  const [showAddToCart, setShowAddToCart] = useState(false);

  const [welcomeBackModalDismissed, setWelcomeBackModalDismissed] =
    useState(false);
  const storedDismissedValue = useSessionStorage(
    WELCOME_BACK_MODAL_DISMISSED_KEY,
  );
  const checkoutUrl = useBuildCommerceUrl(CommercePath.PaymentRedirect);

  useEffect(() => {
    if (storedDismissedValue) {
      setWelcomeBackModalDismissed(true);
    }
  }, [storedDismissedValue]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasCartItems = shouldShowBadge;

    // Show welcomeModal if:
    // 1. Welcome back modal is enabled
    // 2. User has items in cart
    // 3. Modal hasn't been dismissed this session
    // 4. User is on a page where we want to show the modal (not checkout or choose your track)
    // 5. Add to Cart Modal is disabled or isn't open

    if (
      welcomeBackModalEnabled &&
      hasCartItems &&
      !window.location.pathname.includes("/checkout") &&
      !window.location.pathname.includes("/track-select") &&
      !welcomeBackModalDismissed &&
      (!addedToCartModalEnabled || !showAddToCart)
    ) {
      setShowWelcome(true);
      return;
    }
  }, [
    addedToCartModalEnabled,
    welcomeBackModalEnabled,
    welcomeBackModalDismissed,
    showAddToCart,
    shouldShowBadge,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hasCartItems = shouldShowBadge;

    // Show addToCartModal if:
    // 1. Added to cart modal is enabled
    // 2. User has items in cart
    // 3. User has added items in cart
    // 4. User is on a page where we want to show the modal (not checkout)
    // 5. WelcomeBackModal is disabled or has been dismissed
    if (
      addedToCartModalEnabled &&
      hasCartItems &&
      addedToCart &&
      !window.location.pathname.includes("/checkout") &&
      (!welcomeBackModalEnabled || !showWelcome)
    ) {
      setShowAddToCart(true);
      return;
    }
  }, [
    addedToCartModalEnabled,
    welcomeBackModalEnabled,
    showWelcome,
    shouldShowBadge,
    addedToCart,
  ]);

  useEffect(() => {
    if (showWelcome || showAddToCart) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [showWelcome, showAddToCart]);

  const dismissModal = () => {
    setShowWelcome(false);
    setShowAddToCart(false);
    setAddedToCart(false);
    setIsOpen(false);
    setWelcomeBackModalDismissed(true);
    if (typeof window !== "undefined") {
      sessionStorage.setItem(WELCOME_BACK_MODAL_DISMISSED_KEY, "true");
    }
  };

  const handleContinueToCheckout = () => {
    window.location.href = checkoutUrl;
  };

  const value = {
    showWelcome,
    showAddToCart,
    getTotalItems,
    dismissModal,
    handleContinueToCheckout,
    isOpen,
    cartData,
    setAddedToCartModalEnabled,
    setWelcomeBackModalEnabled,
  };

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}
